import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Form,
  InputGroup,
  Badge,
  Alert,
  Dropdown,
  Modal,
  Spinner,
  ButtonGroup,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import {
  Package,
  Plus,
  Search,
  RefreshCw,
  Filter,
  Eye,
  Edit,
  Trash2,
  Tag,
  Printer,
  BarChart3,
  X,
  Briefcase,
  Package2,
  Grid3x3,
  List,
  SortAsc,
  SortDesc,
  AlertTriangle,
  FileSpreadsheet,
  FileText,
  Download,
   Archive,
  RotateCcw,
} from "lucide-react";
import ArchiveConfirmModal from "./ArchiveConfirmModal"; // ✅ NOUVEAU
import DeleteConfirmModal from "./DeleteConfirmModal"; // ✅ Import du modal de confirmation
import * as XLSX from "xlsx";
import { exportProducts } from "../../services/productService";

import productService, { PRODUCT_TYPES } from "../../services/productService";
import categoryService from "../../services/categoryService";
import ProductCard from "./ProductCard";
import CreateProductModal from "./CreateProductModal";
import CreateCategoryModal from "./CreateCategoryModal";
import ProductDetailModal from "./ProductDetailModal";
import ProductLabelGenerator from "./ProductLabelGenerator";
import { useAuth } from "../../contexts/AuthContext";
import { labelService } from "../../services/labelService";
import { use } from "react";
import { useNavigate } from "react-router-dom";

const ProductsList = () => {
  // États existants
  const [products, setProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");

  // ✅ NOUVEAU : Filtre par type de produit
  const [selectedProductType, setSelectedProductType] = useState("all");
  const [showStockFilter, setShowStockFilter] = useState(false);
  const [stockStatus, setStockStatus] = useState("all"); // all, in-stock, low-stock, out-of-stock

  const navigate = useNavigate(); // ✅ AJOUTER CETTE LIGNE

  // Dans le composant, ajouter les états pour l'export
  const [exporting, setExporting] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportFormat, setExportFormat] = useState("excel");

  // Vue et tri
  const [viewMode, setViewMode] = useState("grid"); // grid, list
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");

  const [pagination, setPagination] = useState({
    currentPage: 0,
    totalPages: 1,
    totalElements: 0,
    size: 12,
  });

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [deletingProduct, setDeletingProduct] = useState(false);

  // ✅ NOUVEAU : Archivage
  const [statusFilter, setStatusFilter] = useState("active"); // "active" | "archived"
  const [archivedCount, setArchivedCount] = useState(0);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [productToArchive, setProductToArchive] = useState(null);
  const [archivingProduct, setArchivingProduct] = useState(false);
  const [restoringId, setRestoringId] = useState(null);

  // États pour les modales
  const [showCreateProduct, setShowCreateProduct] = useState(false);
  const [showCreateCategory, setShowCreateCategory] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);

  // États pour l'étiquetage
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [showLabelGenerator, setShowLabelGenerator] = useState(false);
  const [labelStats, setLabelStats] = useState(null);

  const { user } = useAuth();
  const isManager = user?.role === "MANAGER";

  // ✅ NOUVEAU : Vue courante
  const isArchivedView = statusFilter === "archived";

  // Charger les données
  useEffect(() => {
    loadCategories();
    if (isManager) {
      loadBarcodeStats();
      loadArchivedCount();
    }
  }, []);

  // ✅ NOUVEAU : Chargement initial + bascule Actifs <-> Archives
  // (loadProducts() a été retiré de l'effet de montage pour éviter un double appel)
  useEffect(() => {
    setSelectedProducts([]);
    loadProducts();
  }, [statusFilter]);

  // Filtrage en temps réel
  useEffect(() => {
    handleFilter();
  }, [
    searchQuery,
    selectedCategory,
    selectedProductType,
    stockStatus,
    sortBy,
    sortOrder,
    allProducts,
  ]);

  /**
   * ✅ NOUVEAU : Fonction de filtrage améliorée avec types
   */
  const handleFilter = () => {
    // ✅ CORRECTION : vider explicitement quand la source est vide.
    // Sans ça, basculer sur "Archives" (liste vide) laissait les
    // produits ACTIFS affichés à l'écran — bug de continuité d'état.
    if (!allProducts.length) {
      setProducts([]);
      return;
    }

    let filtered = [...allProducts];

    // Filtre par recherche textuelle
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (product) =>
          product.name?.toLowerCase().includes(query) ||
          product.description?.toLowerCase().includes(query) ||
          product.sku?.toLowerCase().includes(query) ||
          product.barcode?.toLowerCase().includes(query) ||
          product.categoryName?.toLowerCase().includes(query)
      );
    }

    // Filtre par catégorie
    if (selectedCategory !== "all") {
      filtered = filtered.filter(
        (product) => product.categoryName === selectedCategory
      );
    }

    // ✅ NOUVEAU : Filtre par type de produit
    if (selectedProductType !== "all") {
      filtered = filtered.filter(
        (product) => product.productType === selectedProductType
      );
    }

    // ✅ NOUVEAU : Filtre par statut de stock (seulement pour les produits stockables)
    if (stockStatus !== "all") {
      filtered = filtered.filter((product) => {
        if (product.productType === PRODUCT_TYPES.SERVICE) {
          return stockStatus === "in-stock"; // Les services sont toujours "disponibles"
        }

        const stock = product.currentStock || 0;
        const minThreshold = product.minThreshold || 5;

        switch (stockStatus) {
          case "in-stock":
            return stock > minThreshold;
          case "low-stock":
            return stock > 0 && stock <= minThreshold;
          case "out-of-stock":
            return stock === 0;
          default:
            return true;
        }
      });
    }

    // Tri
    filtered.sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case "name":
          aValue = a.name?.toLowerCase() || "";
          bValue = b.name?.toLowerCase() || "";
          break;
        case "price":
          aValue = a.price || 0;
          bValue = b.price || 0;
          break;
        case "stock":
          aValue =
            a.productType === PRODUCT_TYPES.SERVICE
              ? 999999
              : a.currentStock || 0;
          bValue =
            b.productType === PRODUCT_TYPES.SERVICE
              ? 999999
              : b.currentStock || 0;
          break;
        case "category":
          aValue = a.categoryName?.toLowerCase() || "";
          bValue = b.categoryName?.toLowerCase() || "";
          break;
        case "type":
          aValue = a.productType || "";
          bValue = b.productType || "";
          break;
        default:
          aValue = a.createdAt || "";
          bValue = b.createdAt || "";
      }

      if (sortOrder === "desc") {
        return aValue < bValue ? 1 : -1;
      }
      return aValue > bValue ? 1 : -1;
    });

    setProducts(filtered);
  };

  const handleDeleteProduct = async (product) => {
   
    setProductToDelete(product);
    setShowDeleteModal(true);
  };

  const confirmDeleteProduct = async (product) => {
    try {
      
      setDeletingProduct(true);

      const response = await productService.deleteProduct(product.id);

      if (response.success) {

        // Fermer le modal
        setShowDeleteModal(false);
        setProductToDelete(null);

        // Recharger la liste des produits
        await loadProducts();

        // Optionnel : Afficher une notification de succès
        // showNotification('success', 'Produit supprimé', `${product.name} a été supprimé avec succès`);
      } else {
        console.error("❌ Échec suppression:", response.message);
        alert("Erreur lors de la suppression : " + response.message);
      }
    } catch (error) {
      console.error("❌ Erreur lors de la suppression du produit:", error);
      alert("Erreur lors de la suppression du produit. Veuillez réessayer.");
    } finally {
      setDeletingProduct(false);
    }
  };

  // ========================================================
  // ✅ NOUVEAU : ARCHIVAGE / RESTAURATION
  // ========================================================

  const loadArchivedCount = async () => {
    try {
      const count = await productService.getArchivedCount();
      setArchivedCount(count);
    } catch (err) {
      console.error("Erreur comptage archives:", err);
    }
  };

  const handleArchiveProduct = (product) => {
    setProductToArchive(product);
    setShowArchiveModal(true);
  };

  const confirmArchiveProduct = async (product, reason) => {
    try {
      setArchivingProduct(true);

      const response = await productService.archiveProduct(product.id, reason);

      if (response.success) {
        setShowArchiveModal(false);
        setProductToArchive(null);
        await loadProducts();
        await loadArchivedCount();
      } else {
        setError(response.message || "Erreur lors de l'archivage");
      }
    } catch (err) {
      console.error("❌ Erreur archivage:", err);
      setError("Erreur lors de l'archivage. Veuillez réessayer.");
    } finally {
      setArchivingProduct(false);
    }
  };

  const handleRestoreProduct = async (product) => {
    if (!window.confirm(`Restaurer "${product.name}" ?`)) return;

    try {
      setRestoringId(product.id);
      setError(null);

      const response = await productService.restoreProduct(product.id);

      if (response.success) {
        await loadProducts();
        await loadArchivedCount();
      } else {
        setError(response.message || "Erreur lors de la restauration");
      }
    } catch (err) {
      console.error("❌ Erreur restauration:", err);
      setError("Erreur lors de la restauration. Veuillez réessayer.");
    } finally {
      setRestoringId(null);
    }
  };

  /**
   * ✅ NOUVELLE FONCTION : Gérer l'export des produits
   */
  const handleExportProducts = async () => {
    setExporting(true);

    try {
      // Déterminer quels produits exporter
      const productsToExport =
        selectedProducts.length > 0
          ? allProducts.filter((p) => selectedProducts.includes(p.id))
          : products; // ✅ CORRECTION : Utiliser 'products' au lieu de 'getFilteredProducts()'

      if (productsToExport.length === 0) {
        setError("Aucun produit à exporter");
        setExporting(false); // ✅ AJOUT : Important !
        return;
      }


      // ✅ CORRECTION : Préparer les filtres correctement
      const appliedFilters = {
        category: selectedCategory !== "all" ? selectedCategory : null,
        type: selectedProductType !== "all" ? selectedProductType : null,
        stockStatus: stockStatus !== "all" ? stockStatus : null, // ✅ Correction du nom
        search: searchQuery || null,
      };

      // Appeler la fonction d'export importée
      const result = exportProducts(
        productsToExport,
        exportFormat,
        appliedFilters
      );

      if (result.success) {
        // ✅ CORRECTION : setSuccess existe maintenant
        setError(null); // Nettoyer les erreurs

        // Afficher un message de succès temporaire
        alert(`✅ ${result.message}`); // Alternative simple

        setShowExportModal(false);
        setSelectedProducts([]);
      } else {
        setError(result.message);
      }
    } catch (error) {
      console.error("❌ Erreur lors de l'export:", error);
      setError("Erreur lors de l'export des produits");
    } finally {
      setExporting(false);
    }
  };

  /**
   * ✅ Modal de confirmation d'export
   */
  const ExportModal = () => {
    // ✅ CORRECTION : Calcul simple du nombre de produits
    const productsCount =
      selectedProducts.length > 0 ? selectedProducts.length : products.length; // Utiliser directement products

    return (
      <Modal
        show={showExportModal}
        onHide={() => setShowExportModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <Download size={24} className="me-2" />
            Exporter les produits
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info" className="mb-3">
            <div className="d-flex align-items-center mb-2">
              <FileSpreadsheet size={20} className="me-2" />
              <strong>Produits à exporter</strong>
            </div>
            <p className="mb-0">
              {selectedProducts.length > 0 ? (
                <>
                  <Badge bg="primary" className="me-2">
                    {selectedProducts.length}
                  </Badge>
                  produits sélectionnés
                </>
              ) : (
                <>
                  <Badge bg="success" className="me-2">
                    {productsCount}
                  </Badge>
                  produits (selon filtres appliqués)
                </>
              )}
            </p>
          </Alert>

          <Form.Group className="mb-3">
            <Form.Label className="fw-bold">
              <Download size={16} className="me-2" />
              Format d'export
            </Form.Label>
            <div className="border rounded p-3">
              <Form.Check
                type="radio"
                id="format-excel"
                name="exportFormat"
                className="mb-2"
                label={
                  <div>
                    <div className="d-flex align-items-center">
                      <FileSpreadsheet
                        size={18}
                        className="me-2 text-success"
                      />
                      <strong>Excel (.xlsx)</strong>
                      <Badge bg="success" className="ms-2">
                        Recommandé
                      </Badge>
                    </div>
                    <small className="text-muted ms-4">
                      Format riche avec mise en forme et feuilles multiples
                    </small>
                  </div>
                }
                checked={exportFormat === "excel"}
                onChange={() => setExportFormat("excel")}
              />
              <Form.Check
                type="radio"
                id="format-csv"
                name="exportFormat"
                label={
                  <div>
                    <div className="d-flex align-items-center">
                      <FileText size={18} className="me-2 text-primary" />
                      <strong>CSV (.csv)</strong>
                    </div>
                    <small className="text-muted ms-4">
                      Format simple compatible avec tous les systèmes
                    </small>
                  </div>
                }
                checked={exportFormat === "csv"}
                onChange={() => setExportFormat("csv")}
              />
            </div>
          </Form.Group>

          <Alert variant="light" className="mb-0 border">
            <div className="mb-2">
              <strong>📊 Données incluses :</strong>
            </div>
            <Row className="small">
              <Col md={6}>
                <ul className="mb-0">
                  <li>Nom et description</li>
                  <li>Prix (vente et revient)</li>
                  <li>Marges calculées</li>
                  <li>Catégorie et type</li>
                </ul>
              </Col>
              <Col md={6}>
                <ul className="mb-0">
                  <li>Stock et seuils</li>
                  <li>SKU et code-barres</li>
                  <li>Statuts (actif, disponible)</li>
                  <li>Dates de création/MAJ</li>
                </ul>
              </Col>
            </Row>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setShowExportModal(false)}
            disabled={exporting}
          >
            <X size={16} className="me-1" />
            Annuler
          </Button>
          <Button
            variant="success"
            onClick={handleExportProducts}
            disabled={exporting}
          >
            {exporting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Export en cours...
              </>
            ) : (
              <>
                <Download size={16} className="me-2" />
                Exporter {productsCount} produit{productsCount > 1 ? "s" : ""}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    );
  };

  /**
   * ✅ Statistiques par type
   */
  const getProductTypeStats = () => {
    const stockableCount = allProducts.filter(
      (p) => p.productType === PRODUCT_TYPES.STOCKABLE
    ).length;
    const serviceCount = allProducts.filter(
      (p) => p.productType === PRODUCT_TYPES.SERVICE
    ).length;
    const lowStockCount = allProducts.filter(
      (p) =>
        p.productType === PRODUCT_TYPES.STOCKABLE &&
        (p.currentStock || 0) <= (p.minThreshold || 5) &&
        (p.currentStock || 0) > 0
    ).length;
    const outOfStockCount = allProducts.filter(
      (p) =>
        p.productType === PRODUCT_TYPES.STOCKABLE && (p.currentStock || 0) === 0
    ).length;

    return { stockableCount, serviceCount, lowStockCount, outOfStockCount };
  };

  const stats = getProductTypeStats();

  const loadProducts = async (page = 0) => {
    try {
      setLoading(true);
      setError(null);

      // ✅ NOUVEAU : source différente selon la vue
      const response = isArchivedView
        ? await productService.getArchivedProducts()
        : await productService.getProducts({
            page,
            size: pagination.size,
            sortBy: "name",
            sortDirection: "asc",
          });

      if (response.success) {
        const productsData = response.data || [];
        // ✅ FILTRAGE SELON productAccessType de l'utilisateur
        const accessibleProducts = filterProductsByAccess(
          productsData,
          user.productAccessType
        );

        setAllProducts(accessibleProducts);

        if (response.pagination) {
          setPagination(response.pagination);
        }
      } else {
        setError(response.message || "Erreur lors du chargement");
      }
    } catch (err) {
      setError("Erreur de connexion");
    } finally {
      setLoading(false);
    }
  };

  // ✅ FONCTION DE FILTRAGE PAR ACCÈS
  const filterProductsByAccess = (products, productAccessType) => {
    if (!productAccessType || productAccessType === "ALL") {
      return products; // Accès complet
    }

    return products.filter((product) => {
      if (productAccessType === "STOCKABLE") {
        return product.productType === "STOCKABLE";
      }

      if (productAccessType === "SERVICE") {
        return product.productType === "SERVICE";
      }

      return false;
    });
  };

  const loadCategories = async () => {
    try {
      const response = await categoryService.getCategories();
      if (response.success) {
        setCategories(response.data || []);
      }
    } catch (err) {
      console.error("Erreur chargement catégories:", err);
    }
  };


  const loadBarcodeStats = async () => {
    try {
      const response = await labelService.getBarcodeStats();
      if (response.success) {
        setLabelStats(response.data);
      }
    } catch (err) {
      console.error("Erreur stats codes-barres:", err);
    }
  };

  const formatPrice = (price) => {
    if (!price) return "0 FCFA";
    return new Intl.NumberFormat("fr-FR").format(price) + " FCFA";
  };

  const handleProductSelect = (product, isSelected) => {
    if (isSelected) {
      setSelectedProducts((prev) => [...prev, product]);
    } else {
      setSelectedProducts((prev) => prev.filter((p) => p.id !== product.id));
    }
  };

  const resetFilters = () => {
    setSearchQuery("");
    setSelectedCategory("all");
    setSelectedProductType("all");
    setStockStatus("all");
    setSortBy("name");
    setSortOrder("asc");
  };

  // ✅ Interface de rendu
  return (
    <Container fluid className="py-4">
      {/* Header avec statistiques */}
      {/* ✅ NOUVEAU : Badge d'info sur les restrictions */}
      {user?.productAccessType !== "ALL" && (
        <Alert variant="info" className="mb-3">
          <div className="d-flex align-items-center">
            <Package size={18} className="me-2" />
            <div>
              <strong>Accès restreint :</strong> Vous ne pouvez gérer que les
              produits de type{" "}
              <Badge
                bg={user.productAccessType === "STOCKABLE" ? "primary" : "info"}
              >
                {user.productAccessType === "STOCKABLE"
                  ? "📦 Stockables"
                  : "🔧 Services"}
              </Badge>
            </div>
          </div>
        </Alert>
      )}
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-start mb-3">
            <div>
              <h2 className="mb-1">
                <Package className="me-2" size={28} />
                Gestion des Produits
              </h2>
              {user?.productAccessType === "ALL" && (
                <div className="text-muted">
                  Total: {allProducts.length} • Stockables:{" "}
                  {stats.stockableCount} • Services: {stats.serviceCount}
                  {stats.lowStockCount > 0 && (
                    <Badge bg="warning" className="ms-2">
                      <AlertTriangle size={12} className="me-1" />
                      {stats.lowStockCount} stock faible
                    </Badge>
                  )}
                  {stats.outOfStockCount > 0 && (
                    <Badge bg="danger" className="ms-2">
                      {stats.outOfStockCount} rupture
                    </Badge>
                  )}
                </div>
              )}

              {user?.productAccessType === "STOCKABLE" && (
                <div className="text-muted">
                  Total: {allProducts.length} •
                  {stats.lowStockCount > 0 && (
                    <Badge bg="warning" className="ms-2">
                      <AlertTriangle size={12} className="me-1" />
                      {stats.lowStockCount} stock faible
                    </Badge>
                  )}
                  {stats.outOfStockCount > 0 && (
                    <Badge bg="danger" className="ms-2">
                      {stats.outOfStockCount} rupture
                    </Badge>
                  )}
                </div>
              )}

              {user?.productAccessType === "SERVICE" && (
                <div className="text-muted">Total: {allProducts.length}</div>
              )}
            </div>

            {isManager && (
              <div className="d-flex gap-2">
                {selectedProducts.length > 0 && (
                  <Button
                    variant="info"
                    onClick={() => setShowLabelGenerator(true)}
                  >
                    <Printer size={16} className="me-1" />
                    Étiquettes ({selectedProducts.length})
                  </Button>
                )}
                <Button
                  variant="outline-success"
                  onClick={() => setShowCreateCategory(true)}
                >
                  <Tag size={16} className="me-1" />
                  Catégorie
                </Button>
                <div className="d-flex gap-2">
                  <Button
                    variant="success"
                    onClick={() => {
                      setEditingProduct(null);
                      setShowCreateProduct(true);
                    }}
                  >
                    <Plus size={16} className="me-1" />
                    Nouveau produit
                  </Button>

                  {user?.productAccessType === "ALL" && (
                    <Button
                      variant="primary"
                      onClick={() => navigate("/products/import")}
                    >
                      <FileSpreadsheet size={16} className="me-1" />
                      Import multiple
                    </Button>
                  )}
                  {/* ✅ BOUTON EXPORT */}
                  <Button
                    variant="outline-primary"
                    onClick={() => setShowExportModal(true)}
                    disabled={allProducts.length === 0}
                    title={
                      selectedProducts.length > 0
                        ? `Exporter ${selectedProducts.length} produits sélectionnés`
                        : `Exporter ${products.length} produits filtrés`
                    }
                  >
                    <Download size={16} className="me-1" />
                    Exporter
                    {selectedProducts.length > 0 && (
                      <Badge bg="primary" className="ms-1">
                        {selectedProducts.length}
                      </Badge>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </Col>
      </Row>

      {/* ✅ NOUVEAU : Bascule Actifs / Archives */}
      {isManager && (
        <ButtonGroup className="mb-3">
          <Button
            variant={!isArchivedView ? "primary" : "outline-primary"}
            onClick={() => setStatusFilter("active")}
          >
            <Package size={16} className="me-1" />
            Produits actifs
          </Button>
          <Button
            variant={isArchivedView ? "secondary" : "outline-secondary"}
            onClick={() => setStatusFilter("archived")}
          >
            <Archive size={16} className="me-1" />
            Archives
            {archivedCount > 0 && (
              <Badge bg={isArchivedView ? "light" : "secondary"}
                     text={isArchivedView ? "dark" : undefined}
                     className="ms-2">
                {archivedCount}
              </Badge>
            )}
          </Button>
        </ButtonGroup>
      )}

      {/* ✅ Bandeau explicatif en mode archives */}
      {isArchivedView && (
        <Alert variant="secondary" className="d-flex align-items-center">
          <Archive size={18} className="me-2 flex-shrink-0" />
          <div className="small">
            Ces produits n'apparaissent ni à la caisse ni dans le stock.
            Leur historique de ventes est conservé, ainsi que leur SKU et
            leur code-barres qui restent réservés.
          </div>
        </Alert>
      )}

      {/* Filtres avancés */}
      <Card className="mb-4">
        <Card.Body>
          <Row className="g-3">
            {/* Recherche */}
            <Col lg={4}>
              <InputGroup>
                <InputGroup.Text>
                  <Search size={16} />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Rechercher un produit..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <Button
                    variant="outline-secondary"
                    onClick={() => setSearchQuery("")}
                  >
                    <X size={16} />
                  </Button>
                )}
              </InputGroup>
            </Col>

            {/* Catégorie */}
            <Col lg={2}>
              <Form.Select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                <option value="all">Toutes catégories</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.name}>
                    {category.name}
                  </option>
                ))}
              </Form.Select>
            </Col>

            {/* ✅ NOUVEAU : Filtre par type */}
            {user?.productAccessType === "ALL" && (
              <Col lg={2}>
                <InputGroup>
                  <InputGroup.Text>
                    <Filter size={16} />
                  </InputGroup.Text>
                  <Form.Select
                    value={selectedProductType}
                    onChange={(e) => {
                      setSelectedProductType(e.target.value);
                      if (e.target.value === PRODUCT_TYPES.SERVICE) {
                        setStockStatus("all");
                      }
                    }}
                  >
                    <option value="all">Tous types</option>
                    <option value={PRODUCT_TYPES.STOCKABLE}>
                      📦 Produits Stockables
                    </option>
                    <option value={PRODUCT_TYPES.SERVICE}>💼 Services</option>
                  </Form.Select>
                </InputGroup>
              </Col>
            )}

            {/* ✅ NOUVEAU : Filtre par stock (seulement si stockable sélectionné) */}
            {(user.productAccessType === "STOCKABLE" ||
              (user.productAccessType === "ALL" &&
                selectedProductType !== PRODUCT_TYPES.SERVICE)) && (
              <Col lg={2}>
                <Form.Select
                  value={stockStatus}
                  onChange={(e) => setStockStatus(e.target.value)}
                  disabled={selectedProductType === PRODUCT_TYPES.SERVICE}
                >
                  <option value="all">Tous stocks</option>
                  <option value="in-stock">En stock</option>
                  <option value="low-stock">Stock faible</option>
                  <option value="out-of-stock">Rupture</option>
                </Form.Select>
              </Col>
            )}

            {/* Tri */}
            <Col lg={2}>
              <div className="d-flex">
                <Form.Select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  style={{
                    borderRight: "none",
                    borderTopRightRadius: 0,
                    borderBottomRightRadius: 0,
                  }}
                >
                  <option value="name">Nom</option>
                  <option value="price">Prix</option>
                  <option value="stock">Stock</option>
                  <option value="category">Catégorie</option>
                  <option value="type">Type</option>
                </Form.Select>
                <Button
                  variant="outline-secondary"
                  onClick={() =>
                    setSortOrder(sortOrder === "asc" ? "desc" : "asc")
                  }
                  style={{
                    borderLeft: "none",
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                  }}
                >
                  {sortOrder === "asc" ? (
                    <SortAsc size={16} />
                  ) : (
                    <SortDesc size={16} />
                  )}
                </Button>
              </div>
            </Col>
          </Row>

          {/* Actions rapides */}
          <div className="mt-3 d-flex justify-content-between align-items-center">
            <div className="d-flex gap-2">
              <Button
                variant={viewMode === "grid" ? "primary" : "outline-secondary"}
                size="sm"
                onClick={() => setViewMode("grid")}
              >
                <Grid3x3 size={16} />
              </Button>
              <Button
                variant={viewMode === "list" ? "primary" : "outline-secondary"}
                size="sm"
                onClick={() => setViewMode("list")}
              >
                <List size={16} />
              </Button>

              <Button
                variant="outline-secondary"
                size="sm"
                onClick={resetFilters}
              >
                Réinitialiser filtres
              </Button>
            </div>

            <div className="text-muted">
              {products.length} produit{products.length !== 1 ? "s" : ""}{" "}
              affiché{products.length !== 1 ? "s" : ""}
            </div>
          </div>
        </Card.Body>
      </Card>

      {error && (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      )}

      {/* Contenu principal */}
      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" />
          <div className="mt-2">Chargement des produits...</div>
        </div>
      ) : products.length === 0 ? (
        <Row>
          <Col>
            <Card className="text-center py-5">
              <Card.Body>
                <Package size={64} className="text-muted mb-3" />
                <h5>
                  {isArchivedView ? "Aucun produit archivé" : "Aucun produit trouvé"}
                </h5>
                <p className="text-muted mb-3">
                  {isArchivedView
                    ? "Aucun produit n'a été archivé dans cette boutique."
                    : searchQuery ||
                      selectedCategory !== "all" ||
                      selectedProductType !== "all"
                    ? "Aucun produit ne correspond à vos critères de recherche."
                    : "Commencez par créer votre premier produit."}
                </p>
                {isManager && !isArchivedView && (
                  <Button
                    variant="success"
                    onClick={() => {
                      setEditingProduct(null);
                      setShowCreateProduct(true);
                    }}
                  >
                    <Plus size={16} className="me-1" />
                    Créer un produit
                  </Button>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      ) : (
        <Row>
          {products.map((product) => (
            <Col
              key={product.id}
              xl={viewMode === "grid" ? 3 : 12}
              lg={viewMode === "grid" ? 4 : 12}
              md={6}
              className="mb-3"
            >
              <ProductCard
                product={product}
                onEdit={(prod) => {
                  setEditingProduct(prod);
                  setShowCreateProduct(true);
                }}
                onDelete={handleDeleteProduct} // ✅ CORRECTION
                // ✅ NOUVEAU : archivage / restauration
                onArchive={handleArchiveProduct}
                onRestore={handleRestoreProduct}
                isArchivedView={isArchivedView}
                isRestoring={restoringId === product.id}
                onView={(prod) => {
                  setSelectedProduct(prod);
                  setShowDetailModal(true);
                }}
                // ... autres props (inchangées)
                isManager={isManager}
                formatPrice={formatPrice}
                showCheckbox={!!product.barcode && !isArchivedView}
                isSelected={!!selectedProducts.find((p) => p.id === product.id)}
                onSelect={(isSelected) =>
                  handleProductSelect(product, isSelected)
                }
                onGenerateLabel={() => console.log("Generate label:", product)}
                viewMode={viewMode}
              />
            </Col>
          ))}
        </Row>
      )}

      {/* Modales */}
      <CreateProductModal
        show={showCreateProduct}
        onHide={() => {
          setShowCreateProduct(false);
          setEditingProduct(null);
        }}
        categories={categories}
        onSuccess={loadProducts}
        product={editingProduct}
      />

      <CreateCategoryModal
        show={showCreateCategory}
        onHide={() => setShowCreateCategory(false)}
        onSuccess={loadCategories}
      />

      <ProductDetailModal
        show={showDetailModal}
        onHide={() => {
          setShowDetailModal(false);
          setSelectedProduct(null);
        }}
        product={selectedProduct}
        formatPrice={formatPrice}
        isManager={isManager}
        onEdit={(prod) => {
          setShowDetailModal(false);
          setEditingProduct(prod);
          setShowCreateProduct(true);
        }}
        onArchive={(prod) => {
          setShowDetailModal(false);
          handleArchiveProduct(prod);
        }}
        onRestore={async (prod) => {
          setShowDetailModal(false);
          await handleRestoreProduct(prod);
        }}
      />

      {/* ✅ NOUVEAU : Modal de confirmation d'archivage */}
      <ArchiveConfirmModal
        show={showArchiveModal}
        onHide={() => {
          setShowArchiveModal(false);
          setProductToArchive(null);
        }}
        onConfirm={confirmArchiveProduct}
        product={productToArchive}
        loading={archivingProduct}
      />

      {/* ✅ Modal de confirmation de suppression */}
      <DeleteConfirmModal
        show={showDeleteModal}
        onHide={() => {
          setShowDeleteModal(false);
          setProductToDelete(null);
        }}
        onConfirm={confirmDeleteProduct}
        product={productToDelete}
        loading={deletingProduct}
      />

      {/* ✅ AJOUT : Modal d'export */}
      <ExportModal />

      {showLabelGenerator && (
        <ProductLabelGenerator
          show={showLabelGenerator}
          onHide={() => setShowLabelGenerator(false)}
          products={selectedProducts}
          onSuccess={() => setSelectedProducts([])}
        />
      )}
    </Container>
  );
};

export default ProductsList;
